import { useEffect, useState } from "react";
import axios from "axios";
import { History, Youtube, FileText, TrendingUp, Eye, Download, CopyPlus, Trash2, FileDown, FileType2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scoreBadge = (score) => {
  if (score >= 80) return "bg-green-100 text-green-700";
  if (score >= 60) return "bg-yellow-100 text-yellow-700";
  return "bg-red-100 text-red-700";
};

const ActionBtn = ({ icon: Icon, label, onClick, href, danger, testid }) => {
  const cls = `flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${danger ? "text-red-600 hover:bg-red-50" : "text-gray-500 hover:text-purple-700 hover:bg-purple-50"}`;
  if (href) {
    return (
      <a href={href} data-testid={testid} title={label} className={cls} onClick={(e) => e.stopPropagation()}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </a>
    );
  }
  return (
    <button data-testid={testid} title={label} className={cls} onClick={(e) => { e.stopPropagation(); onClick(); }}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
};

export default function HistorySection({ onSelect }) {
  const [items, setItems] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => {
    axios
      .get(`${API}/analysis-history`)
      .then((r) => setItems(r.data.slice(0, 9)))
      .catch(() => {});
  };

  useEffect(load, []);

  const duplicate = async (id) => {
    try {
      await axios.post(`${API}/analysis/${id}/duplicate`);
      load();
    } catch { /* noop */ }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/analysis/${id}`);
      setConfirmDelete(null);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch { /* noop */ }
  };

  if (!items.length) return null;

  return (
    <section data-testid="history-section" className="mb-8">
      <div className="flex items-center gap-2 mb-4">
        <History className="w-5 h-5 text-purple-600" />
        <h2 className="text-xl font-semibold text-gray-800">Mis Análisis</h2>
        <span className="text-sm text-gray-500">— reutiliza, duplica o exporta tus informes</span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <div
            key={item.id}
            data-testid={`history-item-${item.id}`}
            className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-left border-2 border-transparent hover:border-purple-300 overflow-hidden"
          >
            <button onClick={() => onSelect(item.id)} className="w-full text-left">
              {item.thumbnail ? (
                <img src={item.thumbnail} alt={item.title || "Video"} className="w-full h-32 object-cover" />
              ) : (
                <div className="w-full h-20 bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  {item.input_type === "url" ? (
                    <Youtube className="w-8 h-8 text-white" />
                  ) : (
                    <FileText className="w-8 h-8 text-white" />
                  )}
                </div>
              )}
              <div className="p-4 pb-2">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${scoreBadge(item.viral_score)}`}>
                    <TrendingUp className="w-3 h-3" />
                    {item.viral_score}/100
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(item.timestamp).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                </div>
                <p className="font-semibold text-gray-900 text-sm line-clamp-2 group-hover:text-purple-600 transition-colors">
                  {item.title || item.script_preview}
                </p>
                {item.channel && <p className="text-xs text-gray-500 mt-1">{item.channel}</p>}
              </div>
            </button>
            <div className="px-2 pb-2 flex flex-wrap items-center border-t border-gray-50 pt-1.5">
              {confirmDelete === item.id ? (
                <div className="flex items-center gap-2 px-2 py-1">
                  <span className="text-xs text-gray-600">¿Eliminar?</span>
                  <button data-testid={`history-delete-confirm-${item.id}`} onClick={() => remove(item.id)} className="text-xs font-bold text-red-600 hover:underline">Sí</button>
                  <button data-testid={`history-delete-abort-${item.id}`} onClick={() => setConfirmDelete(null)} className="text-xs font-bold text-gray-500 hover:underline">No</button>
                </div>
              ) : (
                <>
                  <ActionBtn icon={Eye} label="Ver" onClick={() => onSelect(item.id)} testid={`history-view-${item.id}`} />
                  <ActionBtn icon={Download} label="PDF" href={`${API}/analysis/${item.id}/pdf`} testid={`history-pdf-${item.id}`} />
                  <ActionBtn icon={FileDown} label="MD" href={`${API}/analysis/${item.id}/markdown`} testid={`history-md-${item.id}`} />
                  <ActionBtn icon={FileType2} label="DOCX" href={`${API}/analysis/${item.id}/docx`} testid={`history-docx-${item.id}`} />
                  <ActionBtn icon={CopyPlus} label="Duplicar" onClick={() => duplicate(item.id)} testid={`history-duplicate-${item.id}`} />
                  <ActionBtn icon={Trash2} label="Eliminar" danger onClick={() => setConfirmDelete(item.id)} testid={`history-delete-${item.id}`} />
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
